import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveZone } from "../api-handler/api-handler";

const ZoneListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [ZoneRecordList, setZoneRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalZone, setTotalZone] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);


  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon for creating Zone
  const handleCreateZoneClick = () => {
    navigate("/Zone");
  };

  //onclick of edit Zone
  const handleEditZoneClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;
    delete _navigationData.Location;

    navigate(
      {
        pathname: "/zone",
        search: createSearchParams({
          zoneLocationId: param.recordResponse.ZoneLocationId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //onclick of confirm of delete popup
  const handleConfirm = async () => {
    let _filterClause = `&zonelocationid=${selectedRow.id}`;
    await fetchAvailablePezode(_filterClause, selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel of delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //onClick of delete in list
  const showDeleteZonePopup = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedZoneChanges = (event) => {
    let _navigationData = { previousPath: "/Zonelist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/


  const setZoneList = (ZoneList) => {
    try {
      ZoneList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _ZoneList = ZoneList.map((item) => {
        return {
          id: item.ZoneLocationId,
          zoneName: item.ZoneName,
          location: item.Location,
          recordResponse: item,
        };
      });

      setZoneRecordList(_ZoneList);
      setTotalZone(_ZoneList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchZoneList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getZoneList",
        "zonelocation"
      );
      setZoneList(_lists);
    } catch (err) {
      alert(err);
    }
  };
  //fetch API to get pezode created with the selected zone
  const fetchAvailablePezode = async (filter, param) => {
    try {
      let _availablePezode = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getPezodeList",
        "pezodein",
        filter
      );

      if (_availablePezode && _availablePezode.length > 0) {
        notifyToast("Pezode combination exists with this zone", ToastType.ERROR);
      } else {
        await deleteZone(param);
      }
    } catch (e) {
      alert(e);
    }
  };

  const formPreviousDataForDelete = (param) => {
    try {
      let _previousState = param.recordResponse;
      _previousState.Deleted = true;
      delete _previousState.CreatedDate;
      delete _previousState.ModifiedDate;
      delete _previousState.CountryCode;
      delete _previousState.BusinessCode;
      delete _previousState.Version;
      delete _previousState.SectorId;
      delete _previousState.Status;
      return _previousState ? _previousState : null;
    } catch (e) {
      alert(e);
    }
  };

  //Delete API call
  const deleteZone = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let EditZoneLocationId = param.id;
      const status = await saveZone(formPreviousDataForDelete(param), {
        EditZoneLocationId,
        settingsConfig
      });

      if (status?.responseStatus === 200) {
        notifyToast("Sent for Approval", ToastType.SUCCESS);
        await FetchZoneList();
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Zone deletion failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "zoneName",
      headerName: t("zoneList.zoneName"),
      flex: 2,
    },
    {
      field: "location",
      headerName: t("zoneList.location"),
      flex: 3,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateZoneClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(ZoneRecordList, "Zonelist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedZoneChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditZoneClick,
      buttonColor: "#EEEEE",
      action: "edit"
    },
    {
      icon: MdDelete,
      onClick: showDeleteZonePopup,
      buttonColor: "#A62B23",
      action: "delete"
    },
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchZoneList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={ZoneRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalZone}
        toolbarHeader={t("zoneList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteZoneConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default ZoneListForm;
